/**
 * Pairing solver for merged house floor plans (ADR 0007).
 *
 * Turns the user's Pairing — "this wall/door in the placed floor is this
 * wall/door in the incoming room" — into the rigid placement transform
 * that positions the incoming Room Plan in the floor's shared frame. The
 * same solver drives the live preview and the Re-align action; the server
 * validates the result for rigidity but never re-solves it.
 *
 * Pure module — no React, no Three.js — mirroring floorPlanGeometry.js.
 *
 * Conventions (matching RoomPlan captured scans and floorPlanGeometry):
 *  - World space: XZ ground plane, +Y up, meters.
 *  - 16-float column-major 4x4 transforms. Element (row r, col c) lives
 *    at index c*4 + r.
 *  - Wall/door local frame: col0 = +X along the surface, col1 = world up,
 *    col2 = the surface normal; translation = surface center.
 */

// US interior stud wall (2x4 + two layers of drywall), meters. Mirrors
// schemas/house_floor_plan.py DEFAULT_WALL_THICKNESS_M.
export const DEFAULT_WALL_THICKNESS_M = 0.114;

// Overlap below max(0.5 m², 5% of the smaller room) is treated as scan
// noise + the deliberate wall-thickness interpenetration — no warning.
export const OVERLAP_WARNING_MIN_M2 = 0.5;
export const OVERLAP_WARNING_FRACTION = 0.05;

export const IDENTITY_TRANSFORM = Object.freeze([
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
]);

/** Column-major product A·B (applies B first, then A). */
export const multiplyMatrices = (a, b) => {
    const out = new Array(16).fill(0);
    for (let c = 0; c < 4; c++) {
        for (let r = 0; r < 4; r++) {
            let acc = 0;
            for (let k = 0; k < 4; k++) acc += a[k * 4 + r] * b[c * 4 + k];
            out[c * 4 + r] = acc;
        }
    }
    return out;
};

/**
 * Inverse of a RIGID transform (orthonormal rotation + translation):
 * R⁻¹ = Rᵀ and t⁻¹ = -Rᵀ·t. Cheap and exact for placement math; not a
 * general matrix inverse.
 */
export const invertRigid = (m) => {
    const r = (row, col) => m[col * 4 + row];
    const tx = m[12];
    const ty = m[13];
    const tz = m[14];
    return [
        r(0, 0), r(0, 1), r(0, 2), 0,
        r(1, 0), r(1, 1), r(1, 2), 0,
        r(2, 0), r(2, 1), r(2, 2), 0,
        -(r(0, 0) * tx + r(1, 0) * ty + r(2, 0) * tz),
        -(r(0, 1) * tx + r(1, 1) * ty + r(2, 1) * tz),
        -(r(0, 2) * tx + r(1, 2) * ty + r(2, 2) * tz),
        1,
    ];
};

/** Apply a column-major transform to a 3D point ([x, y] gets z=0). */
export const transformPoint = (m, point) => {
    const [x, y = 0, z = 0] = point;
    return [
        m[0] * x + m[4] * y + m[8] * z + m[12],
        m[1] * x + m[5] * y + m[9] * z + m[13],
        m[2] * x + m[6] * y + m[10] * z + m[14],
    ];
};

const surfaceTransform = (surface) => surface?.transform;

const surfaceHeight = (surface) => {
    const dims = surface?.dimensions;
    return Array.isArray(dims) && Number.isFinite(dims[1]) ? dims[1] : 0;
};

/**
 * Solve a Pairing into the incoming room's placement transform.
 *
 * @param {Object} opts
 * @param {Object} opts.placedSurface - the paired wall/door as served by
 *   the composed floor plan (its transform is already in FLOOR frame).
 * @param {Object} opts.incomingSurface - the counterpart wall/door in the
 *   incoming Room Plan's LOCAL frame (as served by the plan API).
 * @param {number} [opts.thicknessM] - shared-wall separation between the
 *   paired faces.
 * @param {number} [opts.slideM] - lateral offset along the placed wall's
 *   axis (wall↔wall pairings; door↔door pairings leave it 0).
 * @param {boolean} [opts.flipped] - choose the mirror alternative. Scan
 *   normal directions aren't a guaranteed convention, so the default
 *   (wall axes anti-parallel, rooms facing) can come out backwards for
 *   some captures; this toggles the other yaw solution.
 * @param {[number, number, number]} [opts.placedInteriorPoint] - a point
 *   inside the placed ROOM (its floor centroid, floor frame). The
 *   incoming room is pushed to the OPPOSITE side of the shared wall.
 * @returns {number[]} 16-float column-major placement (floor ← room local).
 */
export const solvePairingTransform = ({
    placedSurface,
    incomingSurface,
    thicknessM = DEFAULT_WALL_THICKNESS_M,
    slideM = 0,
    flipped = false,
    placedInteriorPoint = null,
}) => {
    const placed = surfaceTransform(placedSurface);
    const incoming = surfaceTransform(incomingSurface);
    if (!Array.isArray(placed) || placed.length !== 16 || !Array.isArray(incoming) || incoming.length !== 16) {
        return [...IDENTITY_TRANSFORM];
    }

    // Placed surface frame, floor space.
    const alongX = [placed[0], placed[1], placed[2]];
    const normal = [placed[8], placed[9], placed[10]];
    const center = [placed[12], placed[13], placed[14]];

    // Side selection: push the incoming room AWAY from the placed room's
    // interior. Without an interior hint, +normal is as good a guess as
    // any — the preview + flipped/side controls let the user correct.
    let side = 1;
    if (Array.isArray(placedInteriorPoint)) {
        const toInterior =
            (placedInteriorPoint[0] - center[0]) * normal[0] +
            (placedInteriorPoint[2] - center[2]) * normal[2];
        side = toInterior > 0 ? -1 : 1;
    }

    // Target frame the INCOMING surface must occupy. Default: wall axes
    // anti-parallel (the two interior faces of one physical wall point at
    // each other); `flipped` picks the other yaw solution.
    const axisSign = flipped ? 1 : -1;
    const tx = [axisSign * alongX[0], 0, axisSign * alongX[2]];
    const ty = [0, 1, 0];
    // col2 = col0 × col1 keeps the basis right-handed (a reflected basis
    // would be rejected by the server's det=+1 check, by design).
    const tz = [tx[1] * ty[2] - tx[2] * ty[1], tx[2] * ty[0] - tx[0] * ty[2], tx[0] * ty[1] - tx[1] * ty[0]];

    // Vertical: align surface BOTTOMS, not centers — wall heights differ
    // between captures (e.g. 2.91 m vs 2.63 m in real scans), and bottoms
    // sit on the floor in both frames.
    const placedBottomY = center[1] - surfaceHeight(placedSurface) / 2;
    const targetCenterY = placedBottomY + surfaceHeight(incomingSurface) / 2;

    const targetCenter = [
        center[0] + side * normal[0] * thicknessM + alongX[0] * slideM,
        targetCenterY,
        center[2] + side * normal[2] * thicknessM + alongX[2] * slideM,
    ];

    const target = [
        tx[0], tx[1], tx[2], 0,
        ty[0], ty[1], ty[2], 0,
        tz[0], tz[1], tz[2], 0,
        targetCenter[0], targetCenter[1], targetCenter[2], 1,
    ];

    // P maps incoming-local -> floor such that P · S_incoming = target.
    return multiplyMatrices(target, invertRigid(incoming));
};

/**
 * A floor surface's footprint as floor-frame [x, z] points.
 *
 * @param {Object} floorSurface - a `floor` surface dict (polygonCorners in
 *   the surface's local plane + a transform).
 * @param {number[]|null} [placementTransform] - the room's placement; null
 *   when the surface transform is already in floor frame (composed GET).
 */
export const placedFloorPolygon = (floorSurface, placementTransform = null) => {
    const corners = floorSurface?.polygonCorners ?? floorSurface?.polygon_corners;
    const local = surfaceTransform(floorSurface);
    if (!Array.isArray(corners) || !Array.isArray(local) || local.length !== 16) return [];
    const full = placementTransform ? multiplyMatrices(placementTransform, local) : local;
    return corners.map((corner) => {
        const [x, , z] = transformPoint(full, corner);
        return [x, z];
    });
};

const pointInPolygon = ([px, py], polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        const crosses = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
        if (crosses) inside = !inside;
    }
    return inside;
};

const bbox = (polygon) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    polygon.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
    });
    return { minX, minY, maxX, maxY };
};

/**
 * Approximate intersection area (m²) of two 2D polygons by grid sampling.
 *
 * Floor footprints are frequently non-convex (L-shapes), so exact
 * polygon clipping is fiddly; a 5 cm sample grid over the bounding-box
 * intersection is accurate to well under the warning threshold and
 * dependency-free. The grid is capped at ~200×200 cells, coarsening
 * automatically for huge footprints.
 */
export const polygonOverlapArea = (polyA, polyB, { cellM = 0.05 } = {}) => {
    if (polyA.length < 3 || polyB.length < 3) return 0;
    const a = bbox(polyA);
    const b = bbox(polyB);
    const minX = Math.max(a.minX, b.minX);
    const minY = Math.max(a.minY, b.minY);
    const maxX = Math.min(a.maxX, b.maxX);
    const maxY = Math.min(a.maxY, b.maxY);
    if (minX >= maxX || minY >= maxY) return 0;

    const MAX_CELLS_PER_AXIS = 200;
    const cell = Math.max(cellM, (maxX - minX) / MAX_CELLS_PER_AXIS, (maxY - minY) / MAX_CELLS_PER_AXIS);

    let hits = 0;
    for (let x = minX + cell / 2; x < maxX; x += cell) {
        for (let y = minY + cell / 2; y < maxY; y += cell) {
            if (pointInPolygon([x, y], polyA) && pointInPolygon([x, y], polyB)) hits += 1;
        }
    }
    return hits * cell * cell;
};

/**
 * Whether an overlap is worth warning about: above max(0.5 m², 5% of the
 * smaller room). The floor absorbs scan noise (real captures show up to
 * ~29% closure error) and the deliberate wall-thickness interpenetration.
 */
export const isOverlapSignificant = (overlapM2, areaA, areaB) => {
    if (!Number.isFinite(overlapM2) || overlapM2 <= 0) return false;
    const smaller = Math.min(
        Number.isFinite(areaA) && areaA > 0 ? areaA : Infinity,
        Number.isFinite(areaB) && areaB > 0 ? areaB : Infinity,
    );
    const threshold = Number.isFinite(smaller)
        ? Math.max(OVERLAP_WARNING_MIN_M2, smaller * OVERLAP_WARNING_FRACTION)
        : OVERLAP_WARNING_MIN_M2;
    return overlapM2 > threshold;
};

/**
 * The exact `POST /house-floor-plans/<id>/placements` body for a solved
 * pairing — kept in one place so the service, the preview, and the
 * cross-layer schema tests all share a single payload shape.
 */
export const buildPlacementPayload = ({ planId, transform, pairing = null, sortOrder = 0 }) => {
    const body = { plan_id: planId, transform, sort_order: sortOrder };
    if (pairing) {
        body.pairing = {
            placed_plan_id: pairing.placedPlanId,
            placed_surface_uuid: pairing.placedSurfaceUuid,
            surface_uuid: pairing.surfaceUuid,
            thickness_m: pairing.thicknessM ?? DEFAULT_WALL_THICKNESS_M,
            slide_m: pairing.slideM ?? 0,
            flipped: Boolean(pairing.flipped),
        };
    }
    return body;
};
